/**
 * BatchItemsTable — one page of a batch's files: where each one got to,
 * and the error for the ones that did not make it. The error text is the
 * customer's own document naming itself, so it renders in full on hover.
 */

import type { BatchItemResponse } from '@/api/generated/model'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { BatchItemStatusBadge } from './BatchStatusBadge'

const UNITS = ['B', 'KB', 'MB', 'GB']

function formatBytes(bytes: number): string {
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  const digits = unit === 0 ? 0 : 1
  return `${value.toFixed(digits)} ${UNITS[unit] ?? 'B'}`
}

export function BatchItemsTable({ items }: { items: BatchItemResponse[] }): React.JSX.Element {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>File</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="hidden md:table-cell">Stage</TableHead>
          <TableHead className="hidden text-right md:table-cell">Size</TableHead>
          <TableHead>Error</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={item.id}>
            <TableCell className="max-w-[24rem]">
              <div className="truncate font-mono text-xs" title={item.key}>
                {item.key}
              </div>
            </TableCell>
            <TableCell>
              <BatchItemStatusBadge status={item.status} />
            </TableCell>
            <TableCell className="hidden font-mono text-xs text-muted-foreground md:table-cell">
              {item.stage === '' ? '—' : item.stage}
            </TableCell>
            <TableCell className="hidden whitespace-nowrap text-right font-mono text-xs tabular-nums text-muted-foreground md:table-cell">
              {formatBytes(item.size_bytes)}
            </TableCell>
            <TableCell className="max-w-[28rem]">
              {item.error === '' ? (
                <span className="text-muted-foreground">—</span>
              ) : (
                <div className="truncate text-xs text-destructive" title={item.error}>
                  {item.error}
                </div>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
